import { useEffect, useReducer, useRef } from "react";

// The current time as reactive state — the render-pure replacement for
// reading Date.now() during render (which React Compiler treats as impure,
// with unspecified caching behavior). Every refresh re-renders the
// subscriber, so choose the coarsest interval the UI can tolerate; reserve
// "frame" for visibly running stopwatches, since it re-renders every frame.
// Pass enabled=false to pause when nothing time-dependent is in flight; the
// returned time refreshes once when ticking resumes.
export function useNow(refreshMs: number | "frame", enabled = true): number {
  const [now, tick] = useReducer(
    () => Date.now(),
    undefined,
    () => Date.now()
  );
  const resuming = useRef(false);

  useEffect(() => {
    if (!enabled) {
      resuming.current = true;
      return;
    }
    if (resuming.current) {
      resuming.current = false;
      tick();
    }
    if (refreshMs === "frame") {
      let raf = 0;
      const loop = () => {
        tick();
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
      return () => cancelAnimationFrame(raf);
    }
    const timer = setInterval(tick, refreshMs);
    return () => clearInterval(timer);
  }, [refreshMs, enabled]);

  return now;
}
